import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { OutputState } from '../../shared/api/types';
import { CountdownView, presentationFileUrl, SlideView, useCountdown } from './SlideView';
import { usePolling } from './usePolling';
import { usePresentationSocket } from './usePresentationSocket';
import { announceOutput, inDesktopApp, listenForProjection } from './screens';
import { Crossfade } from './Crossfade';
import { isLapsedError, LapsedScreen } from './Lapsed';

const SCREENS = { congregacion: 'congregation', escenario: 'stage' } as const;

/**
 * Qué se ve en la salida, como clave: si cambia (otra diapositiva, logo, negro, conteo, otro fondo
 * o video), la salida hace un fundido cruzado. El conteo que avanza no cuenta como cambio.
 */
export function screenId(s: OutputState, countdownOn: boolean, withBackground = true): string {
  if (s.blackout) return 'negro';
  if (countdownOn) return 'conteo';
  if (s.logo) return `logo:${s.logo.resourceId ?? s.logo.name}`;
  const l = s.live;
  const live = l ? `${l.item}:${l.slide}:${l.text ?? ''}:${l.resource?.id ?? ''}:${l.page ?? ''}:${l.localFile?.name ?? ''}:${l.youtube ?? ''}` : 'nada';
  const bg = withBackground && s.liveBackground ? (s.liveBackground.resource?.id ?? s.liveBackground.localFile?.name ?? '') : '';
  const theme = s.theme ? `${s.theme.background?.id ?? ''}:${s.theme.backgroundPreset ?? ''}` : '';
  return `vivo:${live}|${bg}|${theme}|${s.textHidden ? 'sin-texto' : ''}`;
}

/**
 * Salidas de la presentación, a pantalla completa y sin menús: la de la congregación (solo lo que
 * está al aire) y la de escenario (al aire, lo que sigue, notas, aviso, reloj y temporizador).
 * Si se pierde la conexión, se queda mostrando lo último.
 */
export function OutputPage() {
  const { churchId = '', presentationId = '', screen: screenParam = '' } = useParams();
  const screen = SCREENS[screenParam as keyof typeof SCREENS] ?? 'congregation';
  // Con el socket activo el cambio llega al instante; preguntar cada 5 s queda solo de respaldo.
  const refresh = useRef(() => {});
  const instant = usePresentationSocket({ churchId, presentationId }, () => refresh.current());
  const state = usePolling((signal) => api.get<OutputState>(`/presentation/churches/${churchId}/presentations/${presentationId}/output/${screen}`, signal), instant ? 5000 : 1000, [churchId, presentationId, screen]);
  refresh.current = state.refresh;
  const s = state.data;
  const fileUrl = (rid: string) => presentationFileUrl(churchId, presentationId, rid);
  const countdown = useCountdown(s?.timer?.endsAt ?? null, s?.serverTime ?? null);
  // Con «Quitar», el listón sale deslizándose en lugar de desaparecer de golpe.
  const notice = useLinger(s?.notice ?? null);
  const stageNotice = useLinger(s?.stageNotice ?? null);

  // Abierta con «Proyectar»: la consola le presta el clic para ponerse a pantalla completa.
  useEffect(() => listenForProjection(), []);
  // Avisa a la consola que esta pantalla está abierta (aunque la consola se recargue) y se cierra si se lo pide.
  useEffect(() => announceOutput({ churchId, presentationId }, screenParam === 'escenario' ? 'escenario' : 'congregacion'), [churchId, presentationId, screenParam]);

  useEffect(() => {
    document.title = `${screen === 'stage' ? 'Escenario' : 'Pantalla'} · ${s?.title ?? 'KEORA'}`;
  }, [screen, s?.title]);

  // Suscripción vencida (o iglesia suspendida): Multimedia se apaga, también en las pantallas.
  if (isLapsedError(state.error)) return <LapsedScreen />;

  if (!s) {
    return (
      <div className={`output output--${screen}`}>
        <p className="output__status">{state.error ? 'No se pudo abrir la presentación. Revisa que tengas permiso y conexión.' : 'Conectando…'}</p>
      </div>
    );
  }

  if (screen === 'congregation') {
    return (
      <div className={`output output--congregation${notice.text ? ' output--ticker' : ''}`}>
        <FullscreenButton />
        <Crossfade id={screenId(s, Boolean(s.countdown && countdown))} className="xfade--fill">
          {s.countdown && countdown && !s.blackout ? (
            <CountdownView label={s.timer?.label ?? null} time={countdown} theme={s.theme} logo={s.brand ?? { resourceId: null, name: s.title }} fileUrl={fileUrl} />
          ) : (
            <SlideView slide={s.live} fileUrl={fileUrl} blackout={s.blackout} muted={false} theme={s.theme} logo={s.logo} background={s.liveBackground} hideText={s.textHidden} player={{ playback: s.playback, serverTime: s.serverTime, muted: false }} />
          )}
        </Crossfade>
        {notice.text ? <Ticker text={notice.text} startedAt={s.noticeStartedAt} repeat={s.noticeRepeat} serverTime={s.serverTime} leaving={notice.leaving} /> : null}
        {countdown && countdown !== '0:00' && !s.countdown ? <BigTimer time={countdown} label={s.timer?.label ?? null} /> : null}
      </div>
    );
  }

  if (s.stageFull) {
    // Músicos a pantalla completa: solo la letra o el video; el mensaje a los músicos sigue abajo.
    return (
      <div className={`output output--stage output--stagefull${notice.text ? ' output--ticker' : ''}${stageNotice.text ? ' output--stagemsg' : ''}`}>
        <FullscreenButton />
        <Crossfade id={screenId(s, false)} className="xfade--fill">
          <SlideView slide={s.live} fileUrl={fileUrl} blackout={s.blackout} logo={s.logo} background={s.liveBackground} hideText={s.textHidden} player={{ playback: s.playback, serverTime: s.serverTime, muted: true }} />
        </Crossfade>
        {notice.text ? <Ticker text={notice.text} startedAt={s.noticeStartedAt} repeat={s.noticeRepeat} serverTime={s.serverTime} leaving={notice.leaving} /> : null}
        {stageNotice.text ? <Ticker text={stageNotice.text} tone="stage" startedAt={s.stageNoticeStartedAt} repeat={s.stageNoticeRepeat} serverTime={s.serverTime} leaving={stageNotice.leaving} /> : null}
        {state.error ? <p className="output__offline">Sin conexión</p> : null}
      </div>
    );
  }

  return (
    <div className={`output output--stage${notice.text ? ' output--ticker' : ''}${stageNotice.text ? ' output--stagemsg' : ''}`}>
      <FullscreenButton />
      <div className="stage">
        <div className="stage__main">
          {/* El escenario no usa el tema: letra blanca sobre negro se lee mejor desde lejos. */}
          <Crossfade id={screenId(s, false)}>
            {/* Los músicos ven el video de verdad (el lyric video trae la letra), sin sonido y parejo con la congregación. */}
            <SlideView slide={s.live} fileUrl={fileUrl} blackout={s.blackout} logo={s.logo} background={s.liveBackground} hideText={s.textHidden} player={{ playback: s.playback, serverTime: s.serverTime, muted: true }} />
          </Crossfade>
          {s.notes ? <p className="stage__notes">{s.notes}</p> : null}
        </div>
        <aside className="stage__side">
          <Clock serverTime={s.serverTime} />
          {countdown && countdown !== '0:00' ? (
            <p className="stage__timer">
              <span>{s.timer?.label ?? 'Tiempo'}</span>
              <strong>{countdown}</strong>
            </p>
          ) : null}
          <p className="stage__label">Sigue{s.next?.label ? ` · ${s.next.label}` : ''}</p>
          <SlideView slide={s.next ?? null} fileUrl={fileUrl} />
        </aside>
      </div>
      {notice.text ? <Ticker text={notice.text} startedAt={s.noticeStartedAt} repeat={s.noticeRepeat} serverTime={s.serverTime} leaving={notice.leaving} /> : null}
      {/* El mensaje a los músicos va abajo, grande; pasa las veces elegidas o se queda hasta «Quitar». */}
      {stageNotice.text ? <Ticker text={stageNotice.text} tone="stage" startedAt={s.stageNoticeStartedAt} repeat={s.stageNoticeRepeat} serverTime={s.serverTime} leaving={stageNotice.leaving} /> : null}
      {state.error ? <p className="output__offline">Sin conexión</p> : null}
    </div>
  );
}

/** Segundos que tarda el listón en recorrer el texto una vez (el servidor usa la misma cuenta). */
export const noticeSeconds = (text: string) => Math.max(10, Math.round(text.length * 0.28));

/**
 * Listón con letras que avanzan: rojo arriba para los anuncios; ámbar abajo para el mensaje a los
 * músicos. Con «veces que pasa», todas las salidas cuentan desde la misma hora (la del servidor):
 * van parejas aunque una se recargue, y al terminar la última vuelta el listón se cierra con un fundido.
 */
export function Ticker({ text, tone = 'notice', startedAt, repeat, serverTime, leaving = false }: { text: string; tone?: 'notice' | 'stage'; startedAt?: string | null; repeat?: number | null; serverTime?: string; leaving?: boolean }) {
  const seconds = noticeSeconds(text);
  const start = startedAt ? new Date(startedAt).getTime() : null;
  // Cuánto lleva corriendo se calcula una sola vez, al aparecer el aviso: si se recalculara con cada
  // consulta (la hora del servidor avanza a saltos), las letras brincarían hacia adelante.
  const elapsed = useMemo(() => {
    const offset = serverTime ? new Date(serverTime).getTime() - Date.now() : 0;
    return start !== null ? Math.max(0, (Date.now() + offset - start) / 1000) : 0;
    // Solo al cambiar el aviso o su hora de inicio (no con cada consulta).
  }, [text, startedAt, repeat]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = repeat && start !== null ? repeat * seconds : null;
  const [closing, setClosing] = useState(false);
  const [gone, setGone] = useState(false);
  useEffect(() => {
    setClosing(false);
    setGone(false);
    if (total === null) return;
    // El listón se va justo cuando la última vuelta deja salir la última letra.
    const left = Math.max(0, (total - elapsed) * 1000);
    const t1 = setTimeout(() => setClosing(true), left);
    const t2 = setTimeout(() => setGone(true), left + 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [elapsed, total]);
  if (gone || (!leaving && total !== null && elapsed >= total)) return null;
  const style: CSSProperties =
    total !== null
      ? { animationDuration: `${seconds}s`, animationIterationCount: repeat ?? undefined, animationDelay: `-${elapsed}s`, animationFillMode: 'both' }
      : { animationDuration: `${seconds}s`, animationDelay: start !== null ? `-${elapsed % seconds}s` : undefined };
  return (
    <div className={`ticker ticker--${tone}${closing || leaving ? ' ticker--closing' : ''}`} role="status" aria-label={tone === 'stage' ? 'Mensaje a los músicos' : 'Anuncio'}>
      <span key={`${text}|${startedAt ?? ''}`} className="ticker__text" style={style}>
        {text}
      </span>
    </div>
  );
}

/** Deja el último texto unos instantes después de quitarlo, para que el listón salga con su animación. */
function useLinger(value: string | null, ms = 600): { text: string | null; leaving: boolean } {
  const [last, setLast] = useState(value);
  useEffect(() => {
    if (value !== null) {
      setLast(value);
      return;
    }
    const t = setTimeout(() => setLast(null), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return value !== null ? { text: value, leaving: false } : { text: last, leaving: last !== null };
}

/** Cronómetro grande al centro, sobre fondo oscuro (la letra no se empalma con él). */
export function BigTimer({ time, label }: { time: string; label: string | null }) {
  return (
    <div className="output__bigtimer" role="timer" aria-label={`${label ?? 'Tiempo'} ${time}`}>
      {label ? <p className="output__bigtimer-label">{label}</p> : null}
      <p className="output__bigtimer-time">{time}</p>
    </div>
  );
}

function Clock({ serverTime }: { serverTime: string }) {
  const [now, setNow] = useState(() => new Date());
  const offset = new Date(serverTime).getTime() - Date.now();
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return <p className="stage__clock">{new Date(now.getTime() + offset).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</p>;
}

function FullscreenButton() {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const on = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  if (full || inDesktopApp() || !document.documentElement.requestFullscreen) return null;
  // Abierta con «Proyectar»: un aviso grande, porque cualquier clic en la ventana la pone a pantalla completa.
  const projected = new URLSearchParams(window.location.search).has('proyectar');
  return (
    <button type="button" className={`output__fullscreen${projected ? ' output__fullscreen--big' : ''}`} onClick={() => void document.documentElement.requestFullscreen().catch(() => undefined)}>
      {projected ? 'Haz clic aquí para pantalla completa' : 'Pantalla completa'}
    </button>
  );
}
